import { useCallback, useEffect, useState } from "react";
import { api, useAuth } from "@/lib/auth";
import { useSpace } from "@/lib/space";

// A gate's published layer plus this person's saved answers.
export const useGate = (g) => {
  const { api: prefix } = useSpace();
  const [state, setState] = useState({ loading: true });
  const load = useCallback(() => api.get(`${prefix}/gates/${g}`)
    .then(({ data }) => setState({ data }))
    .catch((e) => setState({ error: e.response?.status === 404 ? "This gate could not be found." : "This gate could not open just now." })), [g, prefix]);
  useEffect(() => {
    setState({ loading: true });
    load();
  }, [load]);
  return { ...state, reload: load };
};

export const localDay = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Today's suggested practice, streak and practice calendar.
export const useToday = () => {
  const { api: prefix } = useSpace();
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`${prefix}/today`, { params: { day: localDay() } }).then(({ data: d }) => alive && setData(d)).catch(() => {});
    return () => {
      alive = false;
    };
  }, [prefix]);
  return data;
};

export const vibrate = (p) => {
  try {
    navigator.vibrate?.(p);
  } catch {
    /* not supported */
  }
};

// Account-wide journey through the 13 gates (done, published, locked), one shared store per app, so the
// home, gate list and gate screens stay in step; refresh() after a check-in.
const EMPTY = { done: [], gates: [] };
const stores = {};
const storeFor = (prefix) => (stores[prefix] ??= { uid: null, data: EMPTY, subs: new Set() });
const emit = (store, data) => {
  store.data = { done: Array.isArray(data?.done) ? data.done : [], gates: Array.isArray(data?.gates) ? data.gates : [] };
  store.subs.forEach((f) => f(store.data));
};
const load = (prefix, uid) => api.get(`${prefix}/journey`).then(({ data }) => storeFor(prefix).uid === uid && emit(storeFor(prefix), data)).catch(() => {});

export const useQuantumJourney = () => {
  const { user } = useAuth();
  const { api: prefix } = useSpace();
  const uid = user?.id;
  const store = storeFor(prefix);
  const [data, setData] = useState(store.uid === uid ? store.data : EMPTY);
  useEffect(() => {
    const s = storeFor(prefix);
    s.subs.add(setData);
    if (uid && s.uid !== uid) {
      s.uid = uid;
      emit(s, {});
    }
    if (uid) load(prefix, uid);
    return () => {
      s.subs.delete(setData);
    };
  }, [uid, prefix]);
  const refresh = useCallback(() => load(prefix, uid), [prefix, uid]);
  return { ...data, refresh };
};

export const fmtTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
